import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, TrendingUp, TrendingDown, Minus } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const W = 220, H = 56, PAD = 4;
const fmtD = (v) => (v == null ? '—' : `${v >= 0 ? '+' : '−'}${Math.abs(v).toFixed(2)}`);
const shortDate = (iso) => new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

function Sparkline({ series, testId }) {
  const known = series.filter((p) => p.deal != null && p.avg != null);
  if (known.length < 2) return <p className="text-[10px] text-slate-400" data-testid={`${testId}-empty`}>Not enough history yet.</p>;
  const vals = known.flatMap((p) => [p.deal, p.avg]);
  const lo = Math.min(...vals) - 0.02, hi = Math.max(...vals) + 0.02;
  const x = (i) => PAD + (i / (series.length - 1)) * (W - 2 * PAD);
  const y = (v) => H - PAD - ((v - lo) / (hi - lo || 1)) * (H - 2 * PAD);
  const path = (key) => series.map((p, i) => (p[key] == null ? null : `${x(i)},${y(p[key])}`)).filter(Boolean)
    .map((pt, i) => `${i === 0 ? 'M' : 'L'}${pt}`).join(' ');
  return (
    <svg width={W} height={H} className="shrink-0" role="img" aria-label="Deal vs portfolio average" data-testid={testId}>
      <path d={path('avg')} fill="none" stroke="#94A3B8" strokeWidth="1.5" strokeDasharray="3 3" />
      <path d={path('deal')} fill="none" stroke="#7C3AED" strokeWidth="2" />
      {series.map((p, i) => (p.deal == null ? null : <circle key={i} cx={x(i)} cy={y(p.deal)} r="1.8" fill="#7C3AED"><title>{`${shortDate(p.date)} · deal ×${p.deal.toFixed(2)} · avg ×${p.avg.toFixed(2)} · ${fmtD(p.vs)}`}</title></circle>))}
    </svg>
  );
}

/** 90-day movement of each flagged factor versus the portfolio average (rebuilt from version snapshots). */
export function BenchmarkTrendPanel({ estimateId, enabled }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!enabled || !estimateId) return;
    setData(null);
    axios.get(`${API_URL}/api/estimates/${estimateId}/benchmark-trend?days=90`, { withCredentials: true })
      .then((r) => setData(r.data)).catch(() => setData({ factors: [], error: true }));
  }, [estimateId, enabled]);
  if (!enabled) return null;
  return (
    <div className="rounded-lg border border-violet-100 bg-violet-50/30 px-3 py-2.5" data-testid="benchmark-trend-panel">
      <p className="text-[10px] font-bold uppercase tracking-wider text-violet-700 mb-1.5">Benchmark trend <span className="font-normal normal-case text-slate-500">· last {data?.days ?? 90} days · <span className="text-violet-700">■</span> this deal vs <span className="text-slate-400">╌</span> portfolio average</span></p>
      {!data && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
      {data?.error && <p className="text-[11px] text-rose-600">Could not load benchmark trend.</p>}
      {data?.factors?.map((f) => {
        const Icon = f.vs_change > 0.005 ? TrendingUp : f.vs_change < -0.005 ? TrendingDown : Minus;
        const tone = f.vs_change > 0.005 ? 'text-rose-600' : f.vs_change < -0.005 ? 'text-emerald-600' : 'text-slate-500';
        const first = f.series.find((p) => p.vs != null);
        return (
          <div key={f.key} className="flex flex-wrap items-center gap-3 py-1.5 border-t border-violet-100 first:border-t-0" data-testid={`benchmark-trend-${f.key}`}>
            <Sparkline series={f.series} testId={`benchmark-trend-spark-${f.key}`} />
            <div className="min-w-0 text-[11px] text-slate-700">
              <p className="font-semibold text-[#0F2040]">{f.label} <span className="font-normal text-slate-500">×{f.multiplier.toFixed(2)} now · avg ×{f.portfolio_avg_multiplier.toFixed(2)}</span></p>
              <p className={`flex items-center gap-1 ${tone}`} data-testid={`benchmark-trend-change-${f.key}`}>
                <Icon className="w-3 h-3" />
                {f.vs_change == null ? 'No earlier snapshot to compare' : (
                  <>Gap vs portfolio {fmtD(f.first_vs)} → <b>{fmtD(f.last_vs)}</b> ({f.vs_change === 0 ? 'unchanged' : `${fmtD(f.vs_change)} since ${shortDate(first.date)}`})</>
                )}
              </p>
              {f.vs_change != null && (
                <p className="text-[10px] text-slate-500">Deal {fmtD(f.deal_change)} · portfolio average {fmtD(f.avg_change)} over the period{f.deal_change === 0 && f.avg_change !== 0 ? ' — the gap moved because the portfolio shifted, not this deal' : ''}{f.deal_change !== 0 && f.avg_change === 0 ? ' — the gap moved because this deal changed' : ''}.</p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
